import { useMemo, useState } from "react";
import { MapPin, Phone, Clock, CalendarDays, Search, Navigation, Stethoscope } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal, Img } from "./Reveal";
import { INDIA_PATHS } from "@/data/indiaPaths";
import { LOCATIONS, LOCATION_IMGS, BRAND } from "@/data/content";
import { useBooking } from "@/lib/booking";
import { scrollToId } from "@/lib/scroll";

const STATUS_STYLE = {
  Operational: "bg-gold text-pine-deep",
  Active: "bg-teal-brand text-white",
  "Network Location": "bg-teal-soft text-teal-brand",
  "Opening Soon": "bg-amber-100 text-amber-800 ring-1 ring-amber-400/60",
  "Coming Soon": "bg-amber-100 text-amber-800 ring-1 ring-amber-400/60",
};

const project = (lat, lng) => ({
  x: (lng - 67.4) * 31.8,
  y: (37.8 - lat) * 30.6,
});

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE["Opening Soon"]}`}>
    {status}
  </span>
);

function IndiaMap({ selectedId, onSelect }) {
  return (
    <div data-testid="india-map" className="relative rounded-[2rem] bg-white border border-pine/10 shadow-soft p-4 md:p-8">
      <svg viewBox="0 0 1000 1000" className="w-full h-auto" role="img" aria-label="Map of Dyntra Clinics locations in India">
        <g>
          {INDIA_PATHS.map((d, i) => (
            <path key={i} d={d} fill="#E3ECE4" stroke="#ffffff" strokeWidth="3" strokeLinejoin="round" />
          ))}
        </g>
        {LOCATIONS.map((loc) => {
          const { x, y } = project(loc.lat, loc.lng);
          const active = selectedId === loc.id;
          const labelLeft = loc.id === "gurgaon";
          const labelRight = loc.id === "noida-75";
          return (
            <g
              key={loc.id}
              data-testid={`map-dot-${loc.id}`}
              onClick={() => onSelect(loc.id)}
              className="cursor-pointer"
              role="button"
              aria-label={`${loc.name}, ${loc.city}`}
            >
              <circle cx={x} cy={y} r={active ? 26 : 18} fill={loc.flagship ? "#C9A24B" : active ? "#0E7C86" : "#1A3626"} opacity={active ? 0.25 : 0.15} />
              {loc.flagship && (
                <circle cx={x} cy={y} r="16" fill="none" stroke="#C9A24B" strokeWidth="3" className="animate-pulse-dot" style={{ transformBox: "fill-box", transformOrigin: "center" }} />
              )}
              <circle cx={x} cy={y} r={active ? 13 : 10} fill={loc.flagship ? "#C9A24B" : active ? "#0E7C86" : "#1A3626"} stroke="#fff" strokeWidth="4" className="transition-all duration-300" />
              <text
                x={labelLeft ? x - 18 : labelRight ? x + 18 : x}
                y={labelLeft || labelRight ? y + 7 : y - 24}
                textAnchor={labelLeft ? "end" : labelRight ? "start" : "middle"}
                className="fill-pine-deep"
                style={{ fontSize: 22, fontWeight: 700 }}
              >
                {loc.city}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="mt-2 text-center text-[11px] text-slate-400">
        Sample network data for illustration — locations are being verified and onboarded.
      </p>
    </div>
  );
}

function ClinicPanel({ loc, onBook }) {
  const bookable = loc.status !== "Opening Soon" && loc.status !== "Coming Soon";
  return (
    <div data-testid="clinic-detail-panel" className="rounded-[2rem] bg-pine-deep text-ivory shadow-grand overflow-hidden grain relative">
      <div className="relative h-44">
        <Img src={loc.img || LOCATION_IMGS[LOCATIONS.indexOf(loc) % LOCATION_IMGS.length]} alt={`${loc.name} clinic`} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-pine-deep to-transparent" />
        <div className="absolute bottom-4 left-6"><StatusBadge status={loc.status} /></div>
      </div>
      <div className="p-7">
        <h3 className="font-display text-2xl font-semibold">{loc.name}</h3>
        <p className="mt-1 text-sm text-gold font-semibold">{loc.city}, {loc.state}</p>
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-ivory/90">
          <Stethoscope className="h-3.5 w-3.5 text-gold" /> Run by {loc.owner}
        </p>
        <div className="mt-5 space-y-2.5 text-sm text-ivory/75">
          <p className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {loc.address}</p>
          <p className="flex items-center gap-2.5"><Clock className="h-4 w-4 shrink-0 text-gold" /> {loc.hours}</p>
          <p className="flex items-center gap-2.5"><Phone className="h-4 w-4 shrink-0 text-gold" /> {loc.phone}</p>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {loc.services.map((s) => (
            <span key={s} className="rounded-full border border-ivory/20 px-3 py-1 text-xs font-semibold text-ivory/80">{s}</span>
          ))}
        </div>
        <div className="mt-7 flex gap-3">
          {bookable ? (
            <button data-testid="panel-book-cta" onClick={() => onBook()} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-3 text-xs font-bold text-pine-deep transition-all duration-300 hover:bg-gold-soft">
              <CalendarDays className="h-4 w-4" /> Book Appointment
            </button>
          ) : (
            <span data-testid="panel-book-disabled" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-3 text-xs font-bold text-ivory/45 cursor-not-allowed" title="This clinic is opening soon — bookings not yet available">
              <CalendarDays className="h-4 w-4" /> Book Now
            </span>
          )}
          <a data-testid="panel-directions-cta" href={BRAND.mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-ivory/25 px-4 py-3 text-xs font-bold text-ivory transition-all duration-300 hover:bg-ivory/10">
            <Navigation className="h-4 w-4" /> Directions
          </a>
        </div>
      </div>
    </div>
  );
}

function Directory({ onSelect, query, setQuery }) {
  const { openBooking } = useBooking();
  const [state, setState] = useState("all");
  const [service, setService] = useState("All");

  const states = useMemo(() => [...new Set(LOCATIONS.map((l) => l.state))].sort(), []);
  const filtered = LOCATIONS.filter((l) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || l.city.toLowerCase().includes(q) || l.name.toLowerCase().includes(q);
    const matchS = state === "all" || l.state === state;
    const matchV = service === "All" || l.services.includes(service);
    return matchQ && matchS && matchV;
  });

  return (
    <div data-testid="clinic-directory" className="mt-20">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-teal-brand">09 — Directory</p>
            <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-pine-deep">Find a Dyntra Clinic Near You</h3>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
              <input
                data-testid="directory-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search city or clinic"
                className="h-11 w-56 rounded-full border border-pine/15 bg-white pl-10 pr-4 text-sm font-medium text-pine-deep placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pine"
              />
            </div>
            <select
              data-testid="directory-state-filter"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="h-11 w-44 rounded-full border border-pine/15 bg-white px-4 text-sm font-semibold text-pine-deep focus:outline-none focus:ring-2 focus:ring-pine cursor-pointer"
            >
              <option value="all">All States</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="flex gap-2">
              {["All", "Dental", "Skin Care"].map((v) => (
                <button
                  key={v}
                  data-testid={`directory-service-${v.toLowerCase().replace(" ", "-")}`}
                  onClick={() => setService(v)}
                  className={`rounded-full px-4 py-2.5 text-xs font-bold border transition-all duration-300 ${service === v ? "bg-pine text-ivory border-pine" : "bg-white text-pine border-pine/15 hover:border-pine/40"}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((loc, i) => {
          const bookable = loc.status !== "Opening Soon" && loc.status !== "Coming Soon";
          return (
          <Reveal key={loc.id} delay={(i % 3) * 0.07}>
            <div data-testid={`directory-card-${loc.id}`} className="group h-full overflow-hidden rounded-3xl bg-white border border-pine/10 shadow-soft transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lift">
              <div className="relative h-40 overflow-hidden">
                <Img src={loc.img || LOCATION_IMGS[LOCATIONS.indexOf(loc) % LOCATION_IMGS.length]} alt={`${loc.name} clinic`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-3 left-3"><StatusBadge status={loc.status} /></div>
              </div>
              <div className="p-5">
                <h4 className="font-display text-lg font-semibold text-pine-deep leading-snug">{loc.name}</h4>
                <p className="mt-1 text-xs font-bold text-teal-brand uppercase tracking-wider">{loc.city}, {loc.state}</p>
                <p className="mt-1.5 text-xs font-semibold text-pine/70">by {loc.owner}</p>
                <div className="mt-3 space-y-1.5 text-xs text-slate-500">
                  <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {loc.address}</p>
                  <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-gold" /> {loc.hours}</p>
                  <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold" /> {loc.phone}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {loc.services.map((s) => (
                    <span key={s} className="rounded-full bg-cream px-2.5 py-1 text-[10px] font-bold text-pine/70">{s}</span>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`directory-view-${loc.id}`} onClick={() => { onSelect(loc.id); scrollToId("clinics"); }} className="flex-1 rounded-full border border-pine/20 px-3 py-2.5 text-xs font-bold text-pine transition-all hover:border-pine">
                    View on Map
                  </button>
                  {bookable ? (
                    <button data-testid={`directory-book-${loc.id}`} onClick={() => openBooking()} className="flex-1 rounded-full bg-pine px-3 py-2.5 text-xs font-bold text-ivory transition-all hover:bg-pine-deep">
                      Book
                    </button>
                  ) : (
                    <span data-testid={`directory-book-disabled-${loc.id}`} title="This clinic is opening soon — bookings not yet available"
                      className="flex-1 rounded-full bg-pine/10 px-3 py-2.5 text-xs font-bold text-pine/40 text-center cursor-not-allowed">
                      Book Now
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <p data-testid="directory-empty" className="mt-10 text-center text-sm text-slate-500">No clinics match your filters yet — the network is growing.</p>
      )}
    </div>
  );
}

export function Clinics() {
  const [selectedId, setSelectedId] = useState("noida-75");
  const [dirQuery, setDirQuery] = useState("");
  const { openBooking } = useBooking();
  const selected = LOCATIONS.find((l) => l.id === selectedId) || LOCATIONS[0];

  const handleMapSelect = (id) => {
    setSelectedId(id);
    const loc = LOCATIONS.find((l) => l.id === id);
    if (loc) setDirQuery(loc.city);
  };

  return (
    <section id="clinics" data-testid="clinics-section" className="py-24 lg:py-32 bg-cream/60 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <SectionHeading
          chapter="08"
          overline="Our Clinic Network"
          title="Growing Across India"
          sub="From our flagship Sector 75, Noida clinic to a planned pan-India franchise network — explore where Dyntra Clinics is headed."
        />
        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-start">
          <Reveal className="lg:col-span-3">
            <IndiaMap selectedId={selectedId} onSelect={handleMapSelect} />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-2 lg:sticky lg:top-32">
            <ClinicPanel loc={selected} onBook={openBooking} />
          </Reveal>
        </div>
        <Directory onSelect={setSelectedId} query={dirQuery} setQuery={setDirQuery} />
      </div>
    </section>
  );
}
